import { LegalLayout, H2, P, UL, LI } from "../components/site/LegalLayout";
import { EMAIL, PHONE_DISPLAY, ADDRESS_LINE1, ADDRESS_LINE2 } from "../lib/site";

export default function SellerPolicy() {
  return (
    <LegalLayout
      title="SCRAP SELLER POLICY"
      updated="June 2026"
      intro="What to bring and what to expect when you sell scrap at Scrapman's. Bring a valid photo ID — we require it for every purchase, no exceptions."
      testid="seller-policy-page"
    >
      <H2>Valid Photo ID Required — Every Time</H2>
      <P>
        We require a current, valid government-issued photo ID for every purchase, regardless of the size or
        value of the load. This applies to first-time and repeat sellers alike. If you cannot present a valid
        photo ID, we cannot buy your material.
      </P>
      <UL>
        <LI>You must be at least 18 years old to sell scrap metal to us.</LI>
        <LI>
          The person selling the material must be the person presenting the ID. We do not purchase from a
          third party on someone else's ID.
        </LI>
        <LI>
          As required by Ohio law, we record seller information and transaction details for each purchase,
          and we may photograph the seller, the vehicle, or the material.
        </LI>
      </UL>

      <H2>Lawful Ownership and Stolen Material</H2>
      <P>
        By selling material to us, you represent that you are the lawful owner of that material or are
        authorized by the owner to sell it. We take metal theft seriously.
      </P>
      <UL>
        <LI>
          We will refuse any load where ownership cannot be reasonably established, and we may hold material
          as required by law.
        </LI>
        <LI>
          We maintain purchase records and cooperate fully with law enforcement investigations, including
          providing records and transaction information when lawfully requested.
        </LI>
        <LI>
          Certain restricted or commonly stolen items may require additional proof of ownership, such as a
          work order, invoice, utility or contractor documentation, or a bill of sale.
        </LI>
      </UL>

      <H2>Vehicles and Titled Items</H2>
      <P>
        For a vehicle or any titled item, you must present the proper title or other documentation required
        by Ohio law along with your photo ID. We cannot purchase a vehicle without the required paperwork.
      </P>

      <H2>Material Preparation and Safety</H2>
      <P>Before you load up, please make sure your material is safe for us to handle:</P>
      <UL>
        <LI>
          All closed containers, tanks, drums, pipes and cylinders must be cut open or clearly emptied and
          visibly inspectable. Sealed containers are a serious explosion hazard and will be refused.
        </LI>
        <LI>
          We do not accept hazardous materials, liquids, fuel tanks with fuel, asbestos-containing material,
          radioactive material, medical or biological waste, ammunition or explosives.
        </LI>
        <LI>
          Appliances and units containing refrigerant, oil, mercury switches or capacitors may require
          certified removal of those components before we can accept them. Call ahead if you are unsure.
        </LI>
        <LI>
          Loads containing significant non-metallic material, trash, dirt or contamination may be downgraded
          or refused.
        </LI>
      </UL>
      <P>
        If you are not sure whether we take something, call {PHONE_DISPLAY} before you load it. It saves
        everyone a trip.
      </P>

      <H2>Weights, Grades and Pricing</H2>
      <UL>
        <LI>
          Prices are based on the current commodity market and change frequently. We do not publish prices —
          call for today's price.
        </LI>
        <LI>
          Payment is based on the verified net weight on our scale and the grade our staff assigns to the
          material after inspection. Our scales are visible to you during weighing.
        </LI>
        <LI>
          Grading is determined by our staff based on material condition, cleanliness, alloy and market
          specifications. Any estimate given in advance, by phone or in response to an online quote request,
          is not a guaranteed price.
        </LI>
        <LI>We reserve the right to refuse any material or any load for any lawful reason.</LI>
      </UL>

      <H2>Payment</H2>
      <P>
        Payment is made after the material is weighed, graded and accepted. The method of payment is at our
        discretion, and for certain materials designated under Ohio law, payment may be issued by check
        rather than cash and may be subject to a waiting period before it can be issued. We will tell you
        which applies to your transaction before we purchase.
      </P>

      <H2>On-Site Safety and Conduct</H2>
      <UL>
        <LI>Follow all posted signs and directions from our staff at all times while on the property.</LI>
        <LI>
          Children and pets must remain in your vehicle. An active scrap yard has heavy equipment, sharp
          material and moving traffic.
        </LI>
        <LI>Closed-toe shoes are strongly recommended. No open flames or smoking in the yard.</LI>
        <LI>
          Enter the yard at your own risk. We may ask anyone who is unsafe, impaired or disruptive to leave.
        </LI>
      </UL>

      <H2>Hours</H2>
      <P>
        Please arrive with enough time to unload before closing. Hours may change for weather, holidays or
        operational reasons — call {PHONE_DISPLAY} if you are traveling a distance.
      </P>

      <H2>Questions</H2>
      <P>
        Scrapmans Metal Recycling LLC
        <br />
        {ADDRESS_LINE1}, {ADDRESS_LINE2}
        <br />
        Phone: {PHONE_DISPLAY}
        <br />
        Email:{" "}
        <a href={`mailto:${EMAIL}`} className="text-red-700 font-semibold underline underline-offset-2">
          {EMAIL}
        </a>
      </P>
      <P>
        This page is a summary of our yard policies for customer convenience. It is not legal advice, and
        applicable federal, state and local requirements control in the event of any conflict.
      </P>
    </LegalLayout>
  );
}
